import { Component, Input, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CountryCodeService } from 'src/app/services/country-code.service';

enum BOOKING_STATUS {
    CONFIRMED = 'Confirmed',
    CANCELED = 'Canceled',
    REFUNDED = 'Refunded',
    PROCESSING = 'Processing',
    PAYMENT_FAILED = 'Payment failed',
    RESCHEDULED = 'Rescheduled',
    PENDING_REFUND = 'Pending refund',
    REFUND_FAILED = 'Refund failed',
    PENDING_PAYMENT = 'Pending payment',
    ORDER_CHANGED = 'Order changed',
}

@Component({
    selector: 'guest-info',
    templateUrl: './guest-info.component.html',
    styleUrls: ['./guest-info.component.scss']
})
export class GuestInfoComponent {
    @Input() type: string;
    @Input() flightDetail: any;
    @Input() passengerList: any;
    @Input() isShowViewDetail: boolean = true;

    tooltipElement: HTMLElement | null = null;
    tooltipWidth: number = 0;
    tooltipHeight: number = 0;
    departSegments: any;
    returnSegments: any;
    passengers: any[] = [];
    isOneWay: boolean;
    targetAddOns: string = '';
    hasVJCodeFlights: boolean = false;
    changeRequestList: any[] = [];
    isShowChooseSchedule: boolean = false;
    historyList: any[] = [];
    chosenSchedule: any;
    isShowFlightDetails: boolean = false;

    constructor(
        private translateService: TranslateService,
        private countryCodeService: CountryCodeService
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['type'] && changes['type'].currentValue) {
            this.isOneWay = this.flightDetail?.flight_info?.one_way;
            this.departSegments = this.flightDetail?.flight_info?.itineraries[0]?.segments;
            if (!this.isOneWay) {
                this.returnSegments = this.flightDetail?.flight_info?.itineraries[1]?.segments;
            }
            this.hasVJCodeFlights = this.flightDetail?.flight_info?.itineraries.some((itinerary: any) => {
                return itinerary?.segments?.[0]?.operating_code === "VJ"
            });

            this.targetAddOns = this.type === 'DEPART' ? 'depart_add_ons' : 'return_add_ons';
            this.passengers = this.passengerList;

            const typeCounter: { [key: string]: number } = {};
            const isHasChangeRequest = this.changeRequestList[this.type === 'DEPART' ? 0 : 1]?.length;
            this.passengers.forEach((passenger) => {
                // Add Index type of passengers
                const type = passenger.passenger_type;
                typeCounter[type] = (typeCounter[type] || 0) + 1;
                passenger.passenger_index_type = typeCounter[type];

                const index = this.type === 'DEPART' ? 0 : 1;
                // Format add-ons departure
                if (index == 0) {
                    passenger.depart_add_ons = this.processAddOns('DEPART', this.departSegments, passenger);
                } else {
                    passenger.return_add_ons = this.processAddOns('RETURN', this.returnSegments, passenger);
                }

                // Handle format change request status
                if (!isHasChangeRequest) {
                    if (Array.isArray(passenger.change_request_history)) {
                        const changeRequestHistory: any[] = passenger.change_request_history;
                        let primaryStatus = '';
                        const uniqueStatusesSet = new Set<string>();

                        for (const item of changeRequestHistory) {
                            const status = item.flight_issue_type?.toUpperCase();
                            if (status !== 'FINDING_FLIGHT') uniqueStatusesSet.add(status);

                            if (status === 'CANCEL_FLIGHT') {
                                primaryStatus = item.flight_issue_type;
                            } else if (!primaryStatus && status !== 'FINDING_FLIGHT') {
                                primaryStatus = item.flight_issue_type;
                            }
                        }

                        this.changeRequestList[index] = this.changeRequestList[index]?.length ? this.changeRequestList[index] : [];
                        this.changeRequestList[index].push({
                            history: changeRequestHistory,
                            primaryStatus: primaryStatus,
                            uniqueStatuses: Array.from(uniqueStatusesSet)
                        });
                    }

                }
            });
        }
    }

    processAddOns(type: string, segments: any[], passenger: any) {
        const addOnsConvert: any = {};

        // Process baggage
        const { extra_baggage_list, departure_extra_baggage, return_extra_baggage } = passenger;
        let baggageList;
        if (extra_baggage_list?.length) {
            baggageList = extra_baggage_list;
        } else if (type === 'DEPART') {
            baggageList = departure_extra_baggage;
        } else if (type === 'RETURN') {
            baggageList = return_extra_baggage;
        }
        baggageList?.forEach((baggage: any) => {
            const key = `${baggage.origin_iata_code}_${baggage.destination_iata_code}`;
            addOnsConvert[key] = addOnsConvert[key] || { seat: null, extra_baggage: null, meals: null };
            addOnsConvert[key].extra_baggage = {
                weight: baggage.weight,
                unit: baggage.unit,
                description: baggage.description
            };
        });

        // Process seat
        const { passenger_seat_list, departure_seat, return_seat } = passenger;
        let seatList;
        if (passenger_seat_list?.length) {
            seatList = passenger_seat_list;
        } else if (type === 'DEPART') {
            seatList = departure_seat;
        } else if (type === 'RETURN') {
            seatList = return_seat;
        }
        seatList?.forEach((seat: any) => {
            const key = `${seat.origin_iata_code}_${seat.destination_iata_code}`;
            addOnsConvert[key] = addOnsConvert[key] || { seat: null, extra_baggage: null, meals: null };
            addOnsConvert[key].seat = seat.code;
        });

        // Process meals
        const { extra_meal_list, departure_meals, return_meals } = passenger;

        let mealList;
        if (extra_meal_list?.length) {
            mealList = extra_meal_list;
        } else if (type === 'DEPART') {
            mealList = departure_meals;
        } else if (type === 'RETURN') {
            mealList = return_meals;
        }
        mealList?.forEach((meal: any) => {
            const key = `${meal.origin_iata_code}_${meal.destination_iata_code}`;
            addOnsConvert[key] = addOnsConvert[key] || { seat: null, extra_baggage: null, meals: null };
            addOnsConvert[key].meals = meal.meals;
        });

        // Sort add-ons follow segments
        const add_ons: any[] = [];
        segments.forEach((segment: any) => {
            const departure = segment.departure.iata_code;
            Object.entries(addOnsConvert).forEach(([key, value]: [string, any]) => {
                const [origin_iata_code, destination_iata_code] = key.split('_');
                if (origin_iata_code === departure) {
                    add_ons.push({
                        origin_iata_code,
                        destination_iata_code,
                        ...value,
                    });
                }
            });
        });

        return add_ons;
    };

    onMouseEnter(event: MouseEvent, frequentFlyerProgram: any, frequentFlyerNumber: any) {
        const x = event.pageX;
        const y = event.pageY;
        const html = `
      <div class="!px-4 !py-3 bg-white rounded-lg text-black font-semibold text-xs shadow-lg opacity-1 max-w-xs">
          <div>
              <p class="font-inter text-xs !font-semibold text-palette-gray-500">${this.translateService.instant('FLIGHT.PASSENGERS.MEMBERSHIP')}</p>
              <div class="font-inter text-sm !font-normal text-palette-gray-900">
                  <img />
                  <span>${frequentFlyerProgram}</span>
                  ${frequentFlyerNumber ? `<span> - </span><span>${frequentFlyerNumber}</span>` : ''}
              </div>
          </div>
      </div>
    `

        if (!this.tooltipElement) {
            this.tooltipElement = document.createElement('div');
            this.tooltipElement.innerHTML = html;
            this.tooltipElement.style.position = 'absolute';
            this.tooltipElement.style.zIndex = '9999';
            document.body.appendChild(this.tooltipElement);
            this.tooltipWidth = this.tooltipElement.offsetWidth;
            this.tooltipHeight = this.tooltipElement.offsetHeight;
        }

        this.tooltipElement.style.left = x - 73 + 'px';
        this.tooltipElement.style.top = y - 77 + 'px';
    }

    onMouseLeave() {
        if (this.tooltipElement && this.tooltipElement.parentNode) {
            this.tooltipElement.parentNode.removeChild(this.tooltipElement);
            this.tooltipElement = null;
        }
    }


    public isNumber(value: any): boolean {
        return typeof value === 'number';
    }

    getRequestStatus(status: string): string {
        switch (status?.toUpperCase()) {
            case "RESCHEDULE_FLIGHT":
                return 'MY_BOOKING.RESCHEDULED';
            case "CHANGE_PASSENGER_NAME":
                return 'MY_BOOKING.CHANGED_NAME';
            case "ADDITIONAL_PURCHASING":
                return 'MY_BOOKING.PURCHASED_SERVICE';
            case "CANCEL_FLIGHT":
                return 'MY_BOOKING.CANCELED';
            default:
                return '';
        }
    }

    handleViewDetails(history: any) {
        if (history.length === 1 && history[0].flight_issue_type !== 'FINDING_FLIGHT') {
            this.chosenSchedule = history[0];
            this.isShowFlightDetails = true;
        } else {
            this.historyList = history;
            this.isShowChooseSchedule = true;
        }
    }

    handleCloseChooseSchedule() {
        this.chosenSchedule = null;
        this.isShowChooseSchedule = false;
    }

    handleConfirmChooseSchedule() {
        this.isShowChooseSchedule = false;
        this.isShowFlightDetails = true;
    }

    handleCloseFlightDetails() {
        this.chosenSchedule = null;
        this.isShowFlightDetails = false;
    }

    handleClassBookingStatus(status: string) {
        switch (status) {
            case BOOKING_STATUS.ORDER_CHANGED:
            case BOOKING_STATUS.CONFIRMED:
            case BOOKING_STATUS.RESCHEDULED:
                return 'bg-palette-emerald-50 text-palette-emerald-600';
            case BOOKING_STATUS.CANCELED:
            case BOOKING_STATUS.REFUNDED:
            case BOOKING_STATUS.PENDING_REFUND:
                return 'bg-palette-gray-100 text-palette-gray-700';
            case BOOKING_STATUS.PROCESSING:
                return 'bg-palette-amber-50 text-palette-amber-700';
            case BOOKING_STATUS.REFUND_FAILED:
            case BOOKING_STATUS.PAYMENT_FAILED:
                return 'bg-palette-red-50 text-palette-red-600';
            case BOOKING_STATUS.PENDING_PAYMENT:
                return 'text-palette-orange-600 bg-palette-orange-50';
            default:
                return '';
        }
    }

    getStatus(status: string): string {
        switch (status) {
            case "Pending booking":
                return "COMMON.PENDING_BOOKING";
            case "Confirmed":
                return "COMMON.CONFIRMED";
            case "Rescheduled":
                return "COMMON.RESCHEDULED";
            case "Processing":
                return "COMMON.PROCESSING";
            case "Completed":
                return "COMMON.COMPLETED";
            case "Pending refund":
                return "COMMON.PENDING_REFUND";
            case "Refunded":
                return "COMMON.REFUNDED";
            case "Refund failed":
                return "COMMON.REFUND_FAILED";
            case "Canceled":
                return "COMMON.CANCELED";
            case "Payment failed":
                return "COMMON.PAYMENT_FAILED";
            case "Pending payment":
                return "COMMON.PENDING_PAYMENT";
            case "Order changed":
                return "COMMON.ORDER_CHANGED"
            default:
                return status;
        }
    }

    getUTCUser(): string {
        return this.countryCodeService.getUTCOffset();
    }
}
